import type { Ticket, TicketChannel, TicketPriority, TicketStatus } from '@/api/types';
import type { BadgeTone } from '@/components/ui';
import { formatDuration } from '@/lib/format';

export const STATUSES: TicketStatus[] = ['open', 'pending', 'on_hold', 'solved', 'closed'];
export const PRIORITIES: TicketPriority[] = ['urgent', 'high', 'normal', 'low'];
export const CHANNELS: TicketChannel[] = ['email', 'chat', 'app', 'whatsapp', 'api'];

export const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  pending: 'Pending',
  on_hold: 'On hold',
  solved: 'Solved',
  closed: 'Closed',
};

export const STATUS_HELP: Record<TicketStatus, string> = {
  open: 'Needs a reply from us',
  pending: 'Waiting on the customer',
  on_hold: 'Waiting on a third party or internal team',
  solved: 'Resolved — reopens if the customer replies',
  closed: 'Archived',
};

export const STATUS_TONE: Record<TicketStatus, BadgeTone> = {
  open: 'brand',
  pending: 'amber',
  on_hold: 'blue',
  solved: 'green',
  closed: 'neutral',
};

export const PRIORITY_TONE: Record<TicketPriority, BadgeTone> = {
  urgent: 'red',
  high: 'amber',
  normal: 'blue',
  low: 'neutral',
};

/** How priorities read in the panel: stored as low / normal / high / urgent, shown as Low / Medium / High / Urgent. */
export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Medium',
  low: 'Low',
};

export const CHANNEL_LABEL: Record<TicketChannel, string> = {
  email: 'Email',
  chat: 'Live chat',
  app: 'Kollabr app',
  whatsapp: 'WhatsApp',
  api: 'API',
};

export const ACTIVE = 'open,pending,on_hold';

/** First-response SLA state for an unanswered ticket. */
export function slaState(
  t: Pick<Ticket, 'slaDueAt'>,
  now = Date.now(),
): { label: string; tone: BadgeTone } | null {
  if (!t.slaDueAt) return null;
  const mins = (new Date(t.slaDueAt).getTime() - now) / 60_000;
  if (mins < 0) return { label: `Overdue ${formatDuration(-mins)}`, tone: 'red' };
  if (mins < 60) return { label: `Due in ${formatDuration(mins)}`, tone: 'amber' };
  return { label: `Due in ${formatDuration(mins)}`, tone: 'neutral' };
}
